/** @format */

import { Building2, Eye, LogOut, User } from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import Header from "../../components/ui/Header";
import HeroSection from "../../components/ui/HeroSection";
import "../../components/ui/Header.css";
import { useSuperAdmin } from "../../hooks/useSuperAdmin";
import { clearSessionOnLogout } from "../../utils/clearSessionOnLogout";

const navLinkBase =
  "flex items-center gap-2.5 px-4 py-3 rounded-lg text-sm font-medium transition-colors";
const navLinkInactive =
  "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40";
const navLinkActive =
  "text-blue-400 bg-slate-800/60 border border-slate-600/50";

export default function ProfileLayout() {
  const navigate = useNavigate();
  const { signOut } = useAuthenticator();
  const { isSuperAdmin } = useSuperAdmin();

  const handleSignOut = () => {
    navigate("/login", { state: { from: null }, replace: true });
    clearSessionOnLogout();
    signOut();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />
      <main className="pt-24 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <HeroSection
            title="Settings"
            description="Manage your account profile and organization preferences."
            className="mb-6"
          />
        </div>
        <div className="max-w-7xl mx-auto flex gap-8">
          <aside className="w-52 shrink-0">
            <nav className="sticky top-24 flex flex-col gap-1">
              <NavLink
                to="/settings/account"
                end
                className={({ isActive }) =>
                  `${navLinkBase} ${isActive ? navLinkActive : navLinkInactive}`
                }
              >
                <User className="w-4 h-4" />
                Account
              </NavLink>
              <NavLink
                to="/settings/organization"
                className={({ isActive }) =>
                  `${navLinkBase} ${isActive ? navLinkActive : navLinkInactive}`
                }
              >
                <Building2 className="w-4 h-4" />
                Organization
              </NavLink>
              {isSuperAdmin && (
                <NavLink
                  to="/settings/demo-invites"
                  className={({ isActive }) =>
                    `${navLinkBase} ${isActive ? navLinkActive : navLinkInactive}`
                  }
                >
                  <Eye className="h-4 w-4 text-blue-300" />
                  Demo invites
                </NavLink>
              )}
              <div className="mt-4 pt-4 border-t border-slate-600/50">
                <button
                  type="button"
                  onClick={handleSignOut}
                  className={`${navLinkBase} w-full ${navLinkInactive} text-left`}
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </nav>
          </aside>
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
